import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { keys } from '@/lib/queryKeys';
import {
  fetchRetentionPolicy, saveRetentionPolicy,
  fetchErasureRequests, fetchPatientErasureRequests,
  createErasureRequest, reviewErasureRequest, completeErasureRequest,
  revealErasedIdentity,
} from './api';
import type { ErasureRequestStatus } from './types';

export function useRetentionPolicy() {
  return useQuery({ queryKey: keys.admin.dataRetention(), queryFn: fetchRetentionPolicy });
}

export function useSaveRetentionPolicy() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { retention_years?: number; policy_notes?: string }) => saveRetentionPolicy(data),
    onSuccess: (data) => qc.setQueryData(keys.admin.dataRetention(), data),
  });
}

export function useErasureRequests(status?: ErasureRequestStatus) {
  return useQuery({ queryKey: keys.admin.erasureRequests(status), queryFn: () => fetchErasureRequests(status) });
}

export function usePatientErasureRequests(uhid: string) {
  return useQuery({
    queryKey: keys.patients.erasureRequests(uhid),
    queryFn: () => fetchPatientErasureRequests(uhid),
    enabled: !!uhid,
  });
}

export function useCreateErasureRequest(uhid: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (reason?: string) => createErasureRequest(uhid, reason),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.patients.erasureRequests(uhid) }),
  });
}

export function useReviewErasureRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, status, resolution_notes }: { id: number; status: 'approved' | 'rejected'; resolution_notes?: string }) =>
      reviewErasureRequest(id, status, resolution_notes),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.admin.erasureRequestsAll() }),
  });
}

export function useCompleteErasureRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => completeErasureRequest(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.admin.erasureRequestsAll() }),
  });
}

/**
 * A mutation, not a query, deliberately — a plain `useQuery` would cache the
 * decrypted identity and could refetch it silently (on refocus, say) without
 * the deliberate "yes, reveal this, here's why" action the audit trail is
 * meant to record. Every call is a fresh, individually-audited reveal.
 */
export function useRevealErasedIdentity() {
  return useMutation({
    mutationFn: ({ id, reason }: { id: number; reason: string }) => revealErasedIdentity(id, reason),
  });
}
